import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { brand } from "@/config/brand";

const IRS_URLS: Record<string, string> = {
  poa: "https://www.irs.gov/tax-professionals/submit-forms-2848-and-8821-online",
  taxpro: "https://www.irs.gov/tax-professionals/tax-pro-account",
};

const PREF_KEY = "eservices:skip-irs-redirect";

interface RedirectIrsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: "poa" | "taxpro";
}

/** "You are leaving <app>" interstitial before opening an IRS page in a new
 * tab. Honors a per-user "don't show again" preference (localStorage). */
export function RedirectIrsDialog({ open, onOpenChange, target }: RedirectIrsDialogProps) {
  const [skip, setSkip] = useState(false);

  const go = () => {
    if (skip) localStorage.setItem(PREF_KEY, "1");
    window.open(IRS_URLS[target], "_blank", "noopener,noreferrer");
    onOpenChange(false);
  };

  // Honor the per-user "don't show again" preference: if it's set, skip the
  // interstitial entirely and open the IRS page as soon as it's triggered.
  useEffect(() => {
    if (open && localStorage.getItem(PREF_KEY) === "1") {
      window.open(IRS_URLS[target], "_blank", "noopener,noreferrer");
      onOpenChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>You are leaving {brand.name}</DialogTitle>
          <DialogDescription>
            This opens the IRS website in a new tab to submit or manage your
            authorization. {brand.name} can't see what you do there.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Checkbox
            id="skip-irs"
            checked={skip}
            onCheckedChange={(v) => setSkip(Boolean(v))}
          />
          <Label htmlFor="skip-irs" className="text-sm">
            Don't show this again
          </Label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={go}>Continue to IRS</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
